import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useTripContext } from './TripDetailPage';
import { tripsApi } from '../../api/trips.api';
import { venuesApi } from '../../api/venues.api';
import { placesApi } from '../../api/places.api';
import { useVenueMap } from '../../hooks/useVenueMap';
import { useUnifiedRoutes } from '../../hooks/useUnifiedRoutes';
import { useCrossDayMove } from '../../hooks/useCrossDayMove';
import { UndoRedoControls } from '../../components/trips/itinerary/UndoRedoControls';
import { useMapsQuotaStore } from '../../stores/mapsQuota.store';
import { dedupeOriginPins, optionalStopIds, routeStopPins, tripPinNums } from '../../utils/routeGroups';
import { usePinFanOut } from '../../hooks/usePinFanOut';
import type { GeoPin } from '../../utils/pinCollision';
import { isPinComfortablyVisible } from '../../utils/mapViewport';
import {
  MAPS_API_KEY, LIBRARIES, MAP_DEFAULT_CENTER, MAP_DEFAULT_ZOOM,
  makeSearchPinIcon,
} from '../../utils/mapConstants';
import LoadingScreen from '../../components/ui/LoadingScreen';
import Spinner from '../../components/ui/Spinner';
import Modal from '../../components/ui/Modal';
import EmptyState from '../../components/ui/EmptyState';
import VenueForm from '../../components/venues/VenueForm';
import AddToItineraryModal from '../../components/venues/AddToItineraryModal';
import { toast } from '../../components/ui/Toast';
import { isAlreadyGone } from '../../api/client';
import RoutesPanel from '../../components/map/RoutesPanel';
import VenueDetailCard from '../../components/map/VenueDetailCard';
import SearchResultCard from '../../components/map/SearchResultCard';
import AiRouteProposalModal from '../../components/map/AiRouteProposalModal';
import VenueCategoryFilterBar from '../../components/map/VenueCategoryFilterBar';
import DayRouteFilterBar from '../../components/map/DayRouteFilterBar';
import MapUnavailable from '../../components/map/MapUnavailable';
import MapQuotaBanner from '../../components/map/MapQuotaBanner';
import { VenuePin, SavedPlaceDot, RouteStopPin } from '../../components/map/MapPins';
import { useRoutePolylines } from '../../hooks/useRoutePolylines';
import { type AiPersistedAction } from '../../components/ai/AiChatPanel';
import { useAiPanel } from '../../components/ai/AiPanelContext';
import {
  MapPin, ExternalLink, Navigation, Search, Route,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown,
  KeyRound, LocateFixed,
} from '../../components/ui/Icon';
import { Icon } from '../../components/ui/Icon';
import { dropVenueStops } from '../../utils/venueStops';
import { fitMapTo } from '../../utils/mapFit';
import { useRouteLegFilter } from '../../hooks/useRouteLegFilter';
import LegChips from '../../components/map/LegChips';
import {
  GoogleMap, useJsApiLoader,
  OverlayViewF, OVERLAY_MOUSE_TARGET,
} from '@react-google-maps/api';

// Width of the floating routes panel (`w-80`) plus its right offset (`right-3`).
const ROUTE_PANEL_WIDTH = 320 + 12;

// Shared empty set: a new one per render would re-run every memo downstream.
const EMPTY_STOPS: Set<string> = new Set();

export default function MapTab() {
  const { trip, days, setDays, history } = useTripContext();
  const { t } = useTranslation();
  const [mode, setMode] = useState<'venues' | 'routes'>('venues');
  const [panelOpen, setPanelOpen] = useState(true);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const filterBarRef = useRef<HTMLDivElement>(null);
  const [filterBarHeight, setFilterBarHeight] = useState(0);
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>(null);
  const [showSavedPlaces, setShowSavedPlaces] = useState(false);
  // Optional stops are on by default: hiding part of the plan is the choice,
  // showing all of it is the plan.
  const [showOptional, setShowOptional] = useState(true);
  // Google calls this global when it refuses the key — expired billing, blown
  // quota, wrong referrer. Without it the map just paints grey and says nothing.
  const [authFailed, setAuthFailed] = useState(false);
  const mapsQuota = useMapsQuotaStore();

  const mapRef     = useRef<google.maps.Map | null>(null);
  const mapWrapRef = useRef<HTMLDivElement | null>(null);
  const hoverTooltipRef = useRef<HTMLDivElement | null>(null);

  const [fsContainer, setFsContainer] = useState<Element>(document.body);
  const [destinationCenter, setDestinationCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [destinationLoaded, setDestinationLoaded] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  // The route panel floats over the map's right edge on desktop only; on mobile
  // it's a bottom sheet, so it must not push the fitBounds padding (which would
  // exceed the phone's width and collapse the map to the minimum zoom).
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 768px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: MAPS_API_KEY ?? '',
    id: 'google-map-script',
    libraries: LIBRARIES,
  });

  useEffect(() => {
    const previous = window.gm_authFailure;
    window.gm_authFailure = () => { setAuthFailed(true); previous?.(); };
    return () => { window.gm_authFailure = previous; };
  }, []);

  // Track fullscreen container
  useEffect(() => {
    const handler = () => setFsContainer(document.fullscreenElement ?? document.body);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Center map on trip destination
  useEffect(() => {
    if (!isLoaded || !trip.destination) { setDestinationLoaded(true); return; }
    if (trip.destinationLat != null && trip.destinationLng != null) {
      setDestinationCenter({ lat: Number(trip.destinationLat), lng: Number(trip.destinationLng) });
      setDestinationLoaded(true);
      return;
    }
    let cancelled = false;
    tripsApi.getCenter(trip.id).then(coords => {
      if (cancelled) return;
      if (coords?.lat != null && coords?.lng != null) setDestinationCenter({ lat: coords.lat, lng: coords.lng });
      setDestinationLoaded(true);
    }).catch(() => { if (!cancelled) setDestinationLoaded(true); });
    return () => { cancelled = true; };
  }, [isLoaded, trip.id, trip.destination, trip.destinationLat, trip.destinationLng]);

  // The routes panel floats over the map's right edge, so that strip of the map
  // is visible to the layout but not to the user.
  const rightInset = !isDesktop || mode === 'venues' || !panelOpen ? 0 : ROUTE_PANEL_WIDTH;

  const legs = useMemo(() => trip.destinations ?? [], [trip.destinations]);
  const venue = useVenueMap({
    tripId: trip.id,
    tripDestination: trip.destination,
    days,
    legs,
    mapRef: mapRef as React.RefObject<google.maps.Map | null>,
    mapWrapRef: mapWrapRef as React.RefObject<HTMLDivElement | null>,
    isLoaded,
    isMapReady: mapReady,
    fsContainer,
    center: destinationCenter,
    rightInset,
  });

  // Re-center if geocode resolves before any venue is pinned
  useEffect(() => {
    if (destinationCenter && mapRef.current && venue.pinned.length === 0) {
      mapRef.current.setCenter(destinationCenter);
      mapRef.current.setZoom(MAP_DEFAULT_ZOOM);
    }
  }, [destinationCenter]); // eslint-disable-line react-hooks/exhaustive-deps

  const routes = useUnifiedRoutes({
    trip, days, setDays: history.setDays, history,
    pinned: venue.pinned,
    isLoaded,
    active: mode === 'routes',
  });
  const routeLeg = useRouteLegFilter({
    days,
    legs,
    enabledDays: routes.enabledDays,
    showOnlyDays: routes.showOnlyDays,
    onFrame: points => { if (mapRef.current) fitMapTo(mapRef.current, points, rightInset); },
  });
  // The same move the itinerary's two views use — a stop dragged onto another
  // day has to mean one thing whichever tab the drag happened in.
  const crossDay = useCrossDayMove({ tripId: trip.id, days, setDays: history.setDays, history });

  // Assistant contexts on the map: 'places' in the venues view, and the trip-wide
  // route agent in the routes view — which also registers as the `places` handler
  // so a venue gIA creates from here lands on this map instead of a tab nobody
  // is looking at.
  useAiPanel(
    mode === 'venues'
      ? { context: 'places', onAction: handleVenuesAiAction }
      : { context: 'allroutes', onAction: handleRoutesAiAction, handles: ['places'] },
  );

  // Saved places that aren't scheduled anywhere. `venueTodays` already answers
  // "which days is this venue on?", so an absent entry means it's on none.
  const unroutedVenues = useMemo(
    () => venue.pinned.filter(v => !venue.venueTodays.has(v.id)),
    [venue.pinned, venue.venueTodays],
  );

  const hiddenOriginPins = useMemo(() => dedupeOriginPins(routes.groups), [routes.groups]);

  /**
   * The stops the optional filter is hiding. This is a **view filter and
   * nothing else**: the route the server computed still runs through them, so
   * the legs that touch one are dimmed rather than removed (see the polyline
   * effect). Recomputing the day without them would be a Routes call per day —
   * ADR-0011 — and that is a separate decision, not a side effect of a chip.
   */
  const optionalStops = useMemo(() => optionalStopIds(routes.groups), [routes.groups]);
  const hiddenStops = useMemo(
    () => (showOptional ? EMPTY_STOPS : optionalStops),
    [showOptional, optionalStops],
  );
  const hiddenPins = useMemo(
    () => (hiddenStops.size === 0 ? hiddenOriginPins : new Set([...hiddenOriginPins, ...hiddenStops])),
    [hiddenOriginPins, hiddenStops],
  );
  // Several days count on from one to the next; a day alone starts at 1.
  const tripNums = useMemo(() => tripPinNums(routes.groups, hiddenPins), [routes.groups, hiddenPins]);

  // Every pin the current view draws, in one list: two stops in the same village
  // hide each other exactly as readily as a stop and a saved place do, so they
  // have to be laid out against each other and not layer by layer. Keys are
  // prefixed because a venue and an activity are different things with ids that
  // live in different tables.
  const fanPoints = useMemo<GeoPin[]>(() => {
    if (mode === 'venues') {
      return venue.filtered.map(v => ({ id: `venue-${v.id}`, lat: v.lat, lng: v.lng }));
    }
    const points = routeStopPins(routes.groups, hiddenPins)
      .map(p => ({ ...p, id: `stop-${p.id}` }));
    if (showSavedPlaces) {
      points.push(...unroutedVenues.map(v => ({ id: `venue-${v.id}`, lat: v.lat, lng: v.lng })));
    }
    return points;
  }, [mode, venue.filtered, routes.groups, hiddenPins, showSavedPlaces, unroutedVenues]);

  const pinFan = usePinFanOut(
    mapRef as React.RefObject<google.maps.Map | null>,
    fanPoints,
    isLoaded && mapReady,
  );

  useRoutePolylines(
    mapRef as React.RefObject<google.maps.Map | null>,
    routes.groups, hiddenStops, isLoaded && mapReady && mode === 'routes',
  );

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    map.setCenter(destinationCenter ?? MAP_DEFAULT_CENTER);
    map.setZoom(MAP_DEFAULT_ZOOM);
    setMapReady(true); // lets the fit-bounds effect re-run now that the map exists
    placesApi.reportMapLoad(trip.id); // measure Google map-load volume/cost
  }, [destinationCenter, trip.id]);

  function openVenueCard(v: typeof venue.pinned[number]) {
    venue.setActivePin(v.id);
    venue.setVenuePanel(v);
    venue.closeSearchResult();
    venue.loadVenueDetails(v);
  }

  /**
   * A stop in the panel is a place on the map: open its card when a saved place
   * backs it, and bring it into view when it isn't already.
   *
   * Only when it isn't. Recentring on a pin the user can already see throws away
   * the frame they had — the neighbouring days, the shape of the route — to
   * arrive somewhere they were already looking. The zoom is never touched: how
   * far out they're looking is their choice, not this function's.
   */
  function selectStop(dayIdx: number, activityId: string) {
    const stop = routes.groups
      .find(g => g.dayIdx === dayIdx)?.stops
      .find(s => s.activity.id === activityId);
    if (!stop) return;

    setSelectedActivityId(activityId);
    if (stop.position) bringIntoView(stop.position);
    if (stop.venue) openVenueCard(stop.venue);
    else { venue.setActivePin(null); venue.setVenuePanel(null); }
  }

  /** Pan to a point only if it isn't already on screen and clear of the panel. */
  function bringIntoView(position: { lat: number; lng: number }) {
    const map = mapRef.current;
    if (!map) return;
    const container = fsContainer !== document.body ? fsContainer : mapWrapRef.current;
    const screen = venue.computePinScreen(position.lat, position.lng);
    const rect = container?.getBoundingClientRect();
    if (screen && rect && isPinComfortablyVisible(screen, rect, { rightInset })) return;
    map.panTo(position);
  }

  // gIA proposing a venue from the map (context 'places'): once accepted, drop
  // its pin and pan to it. Coords were resolved by the AI enrichment, so the
  // pin renders without another Google call.
  function applyVenueActions(actions: AiPersistedAction[]): { lat: number; lng: number } | null {
    let last: { lat: number; lng: number } | null = null;
    for (const action of actions) {
      if (action.type === 'venues_deleted') {
        const gone = new Set(action.venueIds);
        venue.setVenues(prev => prev.filter(v => !gone.has(v.id)));
        // The open popup would otherwise keep showing a place that no longer exists.
        if (venue.venuePanel && gone.has(venue.venuePanel.id)) venue.setVenuePanel(null);
        continue;
      }
      if (action.type !== 'venue_created') continue;
      const v = action.data;
      venue.setVenues(prev => prev.some(x => x.id === v.id) ? prev : [...prev, v]);
      if (v.latitude != null && v.longitude != null) last = { lat: v.latitude, lng: v.longitude };
    }
    return last;
  }

  function handleVenuesAiAction(actions: AiPersistedAction[]) {
    const last = applyVenueActions(actions);
    if (last) {
      mapRef.current?.panTo(last);
      mapRef.current?.setZoom(15);
    }
  }

  /**
   * In the routes view a new place would otherwise be invisible: only stops are
   * drawn. Turn the saved-places layer on so the pin the user just asked for is
   * actually there to click — and to add to a day from its card.
   */
  // The stops sheet floats over the map, but the day-filter bar sits in the
  // flow at the bottom of the column — so the sheet has to clear the tab bar
  // *and* that bar. Measured instead of hardcoded: the chips wrap to a second
  // row on a narrow phone, and a frozen number would cover them again the day
  // someone adds a control to it.
  //
  // Up here with the other hooks, not down by the sheet it serves: this
  // component returns early for a missing API key and again while the venues
  // load, and a hook under those runs on some renders and not others.
  useEffect(() => {
    const el = filterBarRef.current;
    if (!el) {
      setFilterBarHeight(0);
      return;
    }
    const observer = new ResizeObserver(() => setFilterBarHeight(el.offsetHeight));
    observer.observe(el);
    setFilterBarHeight(el.offsetHeight);
    return () => observer.disconnect();
  }, [mode]);

  function handleRoutesAiAction(actions: AiPersistedAction[]) {
    const last = applyVenueActions(actions);
    if (!last) return;
    setShowSavedPlaces(true);
    mapRef.current?.panTo(last);
    mapRef.current?.setZoom(14);
  }

  if (!MAPS_API_KEY) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-8 max-w-sm text-center">
          <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4">
            <Icon as={KeyRound} size={24} className="text-amber-500" />
          </div>
          <h3 className="font-display font-semibold text-lg text-slate-900 dark:text-slate-100 mb-3">Google Maps API key needed</h3>
          <pre className="bg-slate-900 text-teal-300 text-xs rounded-xl px-4 py-3 text-left font-mono">VITE_GOOGLE_MAPS_API_KEY=AIza...</pre>
        </div>
      </div>
    );
  }

  const mapBroken = !!loadError || authFailed;

  if (venue.venuesLoading) return <LoadingScreen />;

  const panelProps = {
    groups: routes.groups,
    destination: trip.destination,
    loading: routes.loading,
    error: routes.error,
    collapsedDays: routes.collapsedDays,
    busyDays: routes.busyDays,
    optimizingDay: routes.optimizingDay,
    legUnavailable: routes.legUnavailable,
    selectedActivityId,
    hiddenStops,
    onToggleCollapsed: routes.toggleCollapsed,
    onSelectStop: selectStop,
    edit: {
      onSetLegMode: routes.setLegMode,
      onReorder: routes.reorderDay,
      onMoveToDay: crossDay.moveToDay,
      onOptimize: routes.optimizeDay,
    },
  };

  const totalStops = routes.groups.reduce((sum, g) => sum + g.stops.length, 0);

  return (
    <div className="h-full flex flex-col">

      {/* ── Toolbar ── */}
      {/* Only the mode/filter pills scroll horizontally: the search box and the
          full-route button stay pinned so they never get clipped by the scroller
          (and the search dropdown isn't cut off by its overflow). */}
      <div className="shrink-0 flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 z-10">
        <div className="flex-1 min-w-0 flex items-center gap-2 sm:gap-3 overflow-x-auto scrollbar-none">
          {/* Mode toggle */}
          <div data-tour="map-modes" className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-xl p-0.5 gap-0.5 shrink-0">
            {(['venues', 'routes'] as const).map(m => (
              <button key={m} data-tour={`map-mode-${m}`} onClick={() => setMode(m)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                  mode === m ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-100' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                }`}>
                {m === 'venues' && <><Icon as={MapPin} size={14} /> {t('map.places')}</>}
                {m === 'routes' && <><Icon as={Route}  size={14} /> {t('map.routes')}</>}
              </button>
            ))}
          </div>

          {mode === 'venues' && (
            <div className="flex items-center gap-1 shrink-0">
              {(['all', 'pending', 'visited'] as const).map(f => (
                <button key={f} onClick={() => venue.setFilter(f)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                    venue.filter === f ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
                  }`}>
                  {f === 'all' ? t('venues.filter.all') : f === 'pending' ? t('venues.filter.toVisit') : t('venues.filter.visited')}
                </button>
              ))}
            </div>
          )}
        </div>

        {mode === 'routes' && <UndoRedoControls history={history} />}

        {mode === 'venues' && isLoaded && fsContainer === document.body && (
          <div className="shrink-0 w-32 sm:w-56 lg:w-72 relative">
            <Icon as={Search} size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 z-10 pointer-events-none" />
            <input
              ref={venue.searchInputRef}
              type="text"
              value={venue.fsQuery}
              onChange={e => venue.setFsQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') { venue.setFsQuery(''); } }}
              placeholder={t('map.searchPlaces')}
              className="w-full pl-8 pr-3 py-1.5 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white dark:focus:bg-slate-700 transition-colors"
            />
            {venue.fsPredictions.length > 0 && (
              <div className="absolute top-full mt-1 w-64 right-0 sm:w-full bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden z-50">
                {venue.fsPredictions.map(p => (
                  <button key={p.placeId} onMouseDown={() => venue.onFsSelect(p.placeId)}
                    className="w-full flex items-start gap-2.5 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left cursor-pointer">
                    <Icon as={MapPin} size={13} className="text-slate-400 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-slate-900 dark:text-slate-100 truncate">{p.mainText}</p>
                      <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">{p.secondaryText}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {mode === 'venues' && venue.routeUrl && (
          <a href={venue.routeUrl} target="_blank" rel="noopener noreferrer"
            title={t('map.fullRoute')} aria-label={t('map.fullRoute')}
            className="btn-primary gap-1.5 text-xs py-1.5 px-2.5 lg:px-4 shrink-0">
            <Icon as={Navigation} size={14} />
            <span className="hidden lg:inline">{t('map.fullRoute')}</span>
            <Icon as={ExternalLink} size={12} className="opacity-70 hidden lg:block" />
          </a>
        )}
      </div>

      {/* ── Fullscreen search portal ── */}
      {mode === 'venues' && isLoaded && fsContainer !== document.body && createPortal(
        <div className="fixed top-3 left-1/2 -translate-x-1/2 z-[9999] w-80 max-w-[calc(100%-2rem)]">
          <div className="relative">
            <Icon as={Search} size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10 pointer-events-none" />
            <input type="text" value={venue.fsQuery} onChange={e => venue.setFsQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') venue.setFsQuery(''); }}
              placeholder={t('map.searchPlaces')}
              className="w-full pl-9 pr-4 py-2.5 bg-white rounded-xl shadow-lg border border-slate-200 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500" />
            {venue.fsPredictions.length > 0 && (
              <div className="absolute top-full mt-1 w-full bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
                {venue.fsPredictions.map(p => (
                  <button key={p.placeId} onMouseDown={() => venue.onFsSelect(p.placeId)}
                    className="w-full flex items-start gap-2.5 px-3 py-2.5 hover:bg-slate-50 transition-colors text-left cursor-pointer">
                    <Icon as={MapPin} size={14} className="text-slate-400 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{p.mainText}</p>
                      <p className="text-xs text-slate-400 truncate">{p.secondaryText}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>,
        fsContainer,
      )}

      {/* ── Body ── */}
      <div className="flex-1 min-h-0 flex overflow-hidden">

        {/* ── Map ── */}
        <div className="flex-1 relative" ref={mapWrapRef}>

          {/* Search result card */}
          {venue.searchResult && !venue.saveOpen && createPortal(
            <SearchResultCard
              searchResult={venue.searchResult}
              screenPos={venue.searchScreenPos}
              onClose={venue.closeSearchResult}
              onCenter={() => {
                mapRef.current?.panTo({ lat: venue.searchResult!.lat, lng: venue.searchResult!.lng });
                mapRef.current?.setZoom(16);
                setTimeout(() => mapRef.current?.panBy(0, 160), 80);
              }}
              onSave={() => venue.setSaveOpen(true)}
            />,
            fsContainer,
          )}

          {/* Venue detail card */}
          {venue.venuePanel && !venue.searchResult && venue.pinScreenPos && createPortal(
            <VenueDetailCard
              venue={venue.venuePanel}
              pinScreenPos={venue.pinScreenPos}
              placeDetail={venue.placeDetails[venue.venuePanel.id]}
              detailsLoading={venue.detailsLoading.has(venue.venuePanel.id)}
              onRevealDetails={() => venue.loadVenueDetails(venue.venuePanel!, { force: true })}
              cardDragOffset={venue.cardDragOffset}
              setCardDragOffset={venue.setCardDragOffset}
              venueTodays={venue.venueTodays}
              onClose={() => { venue.setActivePin(null); venue.setVenuePanel(null); setSelectedActivityId(null); }}
              actions={{
              onToggleVisited: async () => {
                const v = venue.venuePanel!;
                await venuesApi.toggleVisited(trip.id, v.id, !v.visited);
                venue.setVenues(vs => vs.map(x => x.id === v.id ? { ...x, visited: !v.visited } : x));
                venue.setPinned(ps => ps.map(x => x.id === v.id ? { ...x, visited: !v.visited } : x));
                venue.setVenuePanel({ ...v, visited: !v.visited });
              },
              onAddToItinerary: () => { venue.setAddToItineraryVenue(venue.venuePanel); venue.setActivePin(null); venue.setVenuePanel(null); },
              onDelete: async () => {
                const v = venue.venuePanel!;
                try {
                  await venuesApi.delete(trip.id, v.id);
                } catch (e) {
                  if (!isAlreadyGone(e)) { toast(t('common.error'), 'error'); return; }
                }
                venue.setVenues(vs => vs.filter(x => x.id !== v.id));
                venue.setPinned(ps => ps.filter(x => x.id !== v.id));
                // Its stops went with it on the server — drop them here too, or
                // the itinerary keeps a card for a place that is gone.
                setDays(prev => dropVenueStops(prev, v.id));
                venue.setActivePin(null);
                venue.setVenuePanel(null);
                toast(t('venues.deleted'), 'success');
              },
              }}
            />,
            fsContainer,
          )}

          {/* Hover tooltip */}
          {createPortal(
            <div ref={hoverTooltipRef} style={{ display: 'none', position: 'fixed', zIndex: 9990, pointerEvents: 'none', transform: 'translateX(-50%)' }}>
              <div className="hp-name bg-slate-900/90 backdrop-blur-sm text-white text-xs font-semibold px-2.5 py-1.5 rounded-lg shadow-lg whitespace-nowrap" />
              <div style={{ width: 0, height: 0, margin: '0 auto', borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid rgba(15,23,42,0.9)' }} />
            </div>,
            fsContainer,
          )}

          {/* Geocoding progress */}
          {venue.geocoding && mode === 'venues' && (
            <div className="absolute top-16 right-3 z-20 pointer-events-none">
              <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm rounded-xl shadow-md border border-slate-100 dark:border-slate-700 px-3 py-2 flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="w-1.5 h-1.5 rounded-full bg-teal-500"
                      style={{ animation: `dotPulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />
                  ))}
                </div>
                <span className="text-xs font-medium text-slate-600">{venue.pinned.length}/{venue.venues.length}</span>
                <div className="w-14 h-1 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div className="h-full bg-teal-500 rounded-full transition-all"
                    style={{ width: `${(venue.pinned.length / venue.venues.length) * 100}%` }} />
                </div>
              </div>
            </div>
          )}

          {venue.geocoding && venue.pinned.length === 0 && mode === 'venues' && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-50 dark:bg-slate-950">
              <div className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center mx-auto mb-3">
                  <Icon as={MapPin} size={24} className="text-teal-500" />
                </div>
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">{t('map.locatingPlaces')}</p>
                <p className="text-xs text-slate-400">{venue.venues.length} {t('map.locationsToFind')}</p>
              </div>
            </div>
          )}

          {mode === 'routes' && days.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-50 dark:bg-slate-950 z-10">
              <EmptyState icon={Route} title={t('map.noDaysYet')} body={t('map.addDaysFirst')} />
            </div>
          )}

          {/* Route-calculation loading overlay */}
          {isLoaded && destinationLoaded && mode === 'routes' && routes.loading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm pointer-events-none">
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg px-5 py-4 flex items-center gap-3 pointer-events-auto">
                <Spinner size="sm" branded />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('map.calculatingRoutes')}</span>
              </div>
            </div>
          )}

          {mapBroken && (
            <MapUnavailable reason={mapsQuota.exceeded ? 'quota' : 'error'} resetsAt={mapsQuota.resetsAt} />
          )}

          {(!isLoaded || (isLoaded && !destinationLoaded)) && !mapBroken && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-50 dark:bg-slate-950 z-10">
              <Spinner size="lg" />
            </div>
          )}

          {/* The map itself still renders — only the data behind it is cut off. */}
          {!mapBroken && mapsQuota.exceeded && (
            <MapQuotaBanner resetsAt={mapsQuota.resetsAt} />
          )}

          {isLoaded && destinationLoaded && (
            <GoogleMap
              mapContainerClassName="w-full h-full"
              center={destinationCenter ?? MAP_DEFAULT_CENTER}
              zoom={MAP_DEFAULT_ZOOM}
              onLoad={onMapLoad}
              onClick={() => { venue.setActivePin(null); venue.closeSearchResult(); venue.setVenuePanel(null); setSelectedActivityId(null); }}
              options={{
                zoomControl: false, streetViewControl: false,
                mapTypeControl: false, fullscreenControl: true,
                // The route card floats over the top-right corner — keep Google's
                // own controls on the free side instead of behind it.
                fullscreenControlOptions: { position: window.google.maps.ControlPosition.LEFT_TOP },
                cameraControlOptions:     { position: window.google.maps.ControlPosition.LEFT_BOTTOM },
                keyboardShortcuts: false, disableDefaultUI: false,
                styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
              }}
            >
              {/* Venue markers */}
              {mode === 'venues' && venue.filtered.map(v => {
                const fan = pinFan.get(`venue-${v.id}`);
                return (
                  <VenuePin key={v.id} venue={v} fan={fan} active={venue.activePin === v.id}
                    onMouseOver={() => {
                      if (venue.activePin === v.id) return;
                      const pos = venue.computePinScreen(v.lat, v.lng);
                      const el = hoverTooltipRef.current;
                      if (pos && el) {
                        el.style.display = 'block';
                        // The tooltip follows the pin, not the coordinate: a
                        // fanned pin is drawn away from its point, and a label
                        // left behind at the point names the wrong marker.
                        el.style.left = `${pos.x + (fan?.dx ?? 0)}px`;
                        el.style.top = `${pos.y + (fan?.dy ?? 0) - 44}px`;
                        const nameEl = el.querySelector<HTMLElement>('.hp-name');
                        if (nameEl) nameEl.textContent = v.name;
                      }
                    }}
                    onMouseOut={() => { if (hoverTooltipRef.current) hoverTooltipRef.current.style.display = 'none'; }}
                    onClick={() => {
                      if (hoverTooltipRef.current) hoverTooltipRef.current.style.display = 'none';
                      const isOpen = venue.activePin === v.id;
                      venue.setActivePin(isOpen ? null : v.id);
                      venue.setVenuePanel(isOpen ? null : v);
                      venue.closeSearchResult();
                      if (!isOpen) venue.loadVenueDetails(v);
                    }}
                  />
                );
              })}

              {/* Search result pin */}
              {mode === 'venues' && venue.searchResult && (
                <OverlayViewF position={{ lat: venue.searchResult.lat, lng: venue.searchResult.lng }}
                  mapPaneName={OVERLAY_MOUSE_TARGET} getPixelPositionOffset={() => ({ x: -18, y: -48 })} zIndex={999}>
                  <img src={makeSearchPinIcon().url} width={36} height={48} alt="" draggable={false} style={{ display: 'block', userSelect: 'none' }} />
                </OverlayViewF>
              )}

              {/* Saved places that aren't on any route — the layer gIA's new
                  places land in, so they can be clicked and scheduled. */}
              {mode === 'routes' && showSavedPlaces && unroutedVenues.map(v => (
                <SavedPlaceDot key={`saved-${v.id}`} venue={v}
                  fan={pinFan.get(`venue-${v.id}`)} onClick={() => openVenueCard(v)} />
              ))}

              {/* Route stops — numbered within their day, coloured by it */}
              {mode === 'routes' && routes.groups.flatMap(group =>
                group.stops.map(stop => {
                  if (!stop.position || hiddenPins.has(stop.activity.id)) return null;
                  return (
                    <RouteStopPin key={`stop-${stop.activity.id}`}
                      stop={{ ...stop, position: stop.position }} color={group.color}
                      fan={pinFan.get(`stop-${stop.activity.id}`)}
                      num={tripNums?.get(stop.activity.id)}
                      active={selectedActivityId === stop.activity.id}
                      onClick={() => selectStop(group.dayIdx, stop.activity.id)} />
                  );
                }),
              )}
            </GoogleMap>
          )}

          {/* Routes side panel — desktop. Floats over the map so the map shows
                through around it, and collapses to a tab when you just want to look. */}
          {mode === 'routes' && days.length > 0 && (
            <div className={`hidden md:flex absolute top-3 right-3 bottom-3 z-30 flex-col transition-[width] duration-300 ease-out ${panelOpen ? 'w-80' : 'w-11'}`}>
              {panelOpen ? (
                <div className="relative flex-1 min-h-0 flex flex-col overflow-hidden rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-lg shadow-slate-900/5">
                  <button
                    onClick={() => setPanelOpen(false)}
                    aria-label={t('map.collapsePanel')}
                    title={t('map.collapsePanel')}
                    className="absolute top-2.5 right-2.5 z-20 w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                  >
                    <Icon as={ChevronRight} size={15} />
                  </button>
                  <RoutesPanel {...panelProps} />
                </div>
              ) : (
                <button
                  onClick={() => setPanelOpen(true)}
                  aria-label={t('map.expandPanel')}
                  title={t('map.expandPanel')}
                  className="flex-1 min-h-0 w-11 flex flex-col items-center justify-center gap-2 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-lg shadow-slate-900/5 text-slate-500 dark:text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 hover:border-teal-200 dark:hover:border-teal-800 transition-colors cursor-pointer"
                >
                  <Icon as={ChevronLeft} size={15} />
                  <span className="text-[11px] font-bold uppercase tracking-widest [writing-mode:vertical-rl]">
                    {t('map.daysCount', { count: routes.groups.length })}
                  </span>
                  <span className="flex items-center gap-1 text-[11px] font-semibold text-slate-400 dark:text-slate-500">
                    <Icon as={MapPin} size={11} />
                    {totalStops}
                  </span>
                </button>
              )}
            </div>
          )}

          {/* Recenter button */}
          {isLoaded && (
            <button
              onClick={() => { mapRef.current?.panTo(destinationCenter ?? MAP_DEFAULT_CENTER); mapRef.current?.setZoom(MAP_DEFAULT_ZOOM); }}
              className="absolute bottom-24 left-3 z-20 w-9 h-9 bg-white dark:bg-slate-800 rounded-lg shadow-md border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-500 dark:text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 hover:border-teal-300 dark:hover:border-teal-700 transition-colors cursor-pointer"
              title={t('map.recenter')}
            >
              <Icon as={LocateFixed} size={16} />
            </button>
          )}
        </div>

      </div>

      {/* Routes bottom sheet — mobile.

          Floats over the map, and over nothing else: its offset is everything
          below it. The tab bar is `4rem + safe-area-inset-bottom` — the whole
          formula the scroll container reserves in `TripDetailPage`, not the
          bare `4rem`, which is right only on a phone with no home indicator and
          lands ~34 px inside the bar on every iPhone that has one. The day
          filters are in the flow under the map, so their measured height goes
          on top of that.

          `dvh` for the same reason `#root` uses it (index.css): with `vh` the
          open sheet is measured against the viewport Safari would have with its
          toolbars hidden, and grows past the top of the visible one. */}
      {mode === 'routes' && days.length > 0 && (
        <div
          style={{ bottom: `calc(4rem + env(safe-area-inset-bottom) + ${filterBarHeight}px)` }}
          className={`md:hidden fixed left-0 right-0 z-40 bg-white dark:bg-slate-900 rounded-t-2xl shadow-2xl border-t border-slate-200 dark:border-slate-800 flex flex-col overflow-hidden transition-[height] duration-300 ease-out ${mobileSheetOpen ? 'h-[72dvh]' : 'h-auto'}`}>
          <div className="w-full flex flex-col items-center pt-2.5 pb-0 shrink-0">
            <button
              onClick={() => setMobileSheetOpen(o => !o)}
              className="w-full flex justify-center py-1 cursor-pointer"
              aria-label={mobileSheetOpen ? t('map.collapseStops') : t('map.expandStops')}>
              <div className="w-10 h-1 bg-slate-200 dark:bg-slate-700 rounded-full" />
            </button>
            <div className="w-full flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-100 dark:border-slate-800">
              <div className="min-w-0 text-left">
                <p className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-widest truncate">
                  {t('map.daysCount', { count: routes.groups.length })} · {t('map.stopsCount', { count: totalStops })}
                </p>
                {!mobileSheetOpen && <p className="text-[10px] text-slate-400 mt-0.5">{t('map.tapToSeeStops')}</p>}
              </div>
              <button
                onClick={() => setMobileSheetOpen(o => !o)}
                className="shrink-0 w-8 h-8 flex items-center justify-center text-slate-400 cursor-pointer"
                aria-label={mobileSheetOpen ? t('map.collapseStops') : t('map.expandStops')}>
                {mobileSheetOpen ? <Icon as={ChevronDown} size={16} /> : <Icon as={ChevronUp} size={16} />}
              </button>
            </div>
          </div>

          {mobileSheetOpen && (
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              <RoutesPanel {...panelProps} hideHeader />
            </div>
          )}
        </div>
      )}

      {/* Category legend */}
      {mode === 'venues' && venue.pinned.length > 0 && (
        <VenueCategoryFilterBar
          leading={(
            <LegChips
              legs={legs}
              active={venue.legFilter}
              onSelect={i => venue.setLegFilter(cur => (cur === i ? null : i))}
            />
          )}
          venues={venue.pinned}
          selectedCats={venue.selectedCats}
          onToggleCat={venue.toggleCat}
          onShowAll={venue.clearCats}
        />
      )}

      {/* Routes: day filter chips plus the saved-places layer toggle */}
      {mode === 'routes' && (
        <div ref={filterBarRef} className="shrink-0">
        <DayRouteFilterBar
          leading={<LegChips legs={legs} active={routeLeg.active} onSelect={routeLeg.select} />}
          days={days}
          enabledDays={routes.enabledDays}
          onToggleDay={routes.toggleDay}
          onToggleAll={routes.toggleAllDays}
          daysWithRoute={routes.daysWithRoute}
          showOptional={showOptional}
          onToggleOptional={() => setShowOptional(o => !o)}
          optionalCount={optionalStops.size}
          showSavedPlaces={showSavedPlaces}
          onToggleSavedPlaces={() => setShowSavedPlaces(s => !s)}
          savedPlacesCount={unroutedVenues.length}
        />
        </div>
      )}

      {/* Save modal */}
      <Modal open={venue.saveOpen} onClose={() => venue.setSaveOpen(false)} title={t('map.savePlaceModal')}>
        {venue.searchResult && (
          <VenueForm
            tripId={trip.id}
            tripVenues={venue.venues}
            prefill={{ name: venue.searchResult.name, category: 'ATTRACTION', address: venue.searchResult.address || null, mapsUrl: venue.searchResult.mapsUrl }}
            onSaved={venue.handleSavedFromSearch}
            onCancel={() => venue.setSaveOpen(false)}
          />
        )}
      </Modal>

      {/* AI route proposal modal */}
      <AiRouteProposalModal
        proposal={routes.aiProposal}
        currentOrder={
          routes.groups.find(g => g.dayIdx === routes.aiProposal?.dayIdx)?.stops.map(s => s.activity) ?? []
        }
        onAccept={routes.acceptAiProposal}
        onDiscard={routes.discardAiProposal}
      />

      {/* Add to itinerary modal */}
      <Modal open={!!venue.addToItineraryVenue} onClose={() => venue.setAddToItineraryVenue(null)} title={t('map.addToItineraryModal')}>
        {venue.addToItineraryVenue && (
          <AddToItineraryModal
            venue={venue.addToItineraryVenue}
            days={days}
            tripId={trip.id}
            onSaved={({ added, removed }) => {
              setDays(prev => prev.map(d => {
                const toAdd = added.filter(i => i.dayId === d.id).map(i => i.activity);
                const removedIds = new Set(removed.filter(i => i.dayId === d.id).map(i => i.activityId));
                if (toAdd.length === 0 && removedIds.size === 0) return d;
                return {
                  ...d,
                  activities: [...d.activities.filter(a => !removedIds.has(a.id)), ...toAdd],
                };
              }));
              venue.setAddToItineraryVenue(null);
            }}
            onCancel={() => venue.setAddToItineraryVenue(null)}
          />
        )}
      </Modal>
    </div>
  );
}
